import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Image, message, Modal, Row, Tag, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { connect, useDispatch } from 'react-redux';
import RestHelper from 'utils/RestHelper';
import { makeSectionsFromLinesByHeaderRegex } from 'components/exception/issue/RegisterInfo/RegisterInfo';
import { CS_STYLES } from 'utils/constants/style-constants';
import { MdOutlineGpsFixed } from 'react-icons/md';
import { TbTools } from 'react-icons/tb';
import { green } from '@ant-design/colors';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';
import RootCauseIcon from 'svg/v2/newcs_aiassistant_rootcauseanalysis_icon.svg';
import SuggestionIcon from 'svg/v2/newcs_aiassistant_repairsuggestions_icon.svg';
import { ze } from 'utils/zhEn';
import useZuStore from 'store/zustand/zustand-store';
import { AiIssueAnalysisUtil } from 'components/top/AiDrawer/ai-issue-analysis';
import { AiDrawerHooks } from 'components/top/AiDrawer/ai-drawer-hooks';
import { IssueAiStatus, AiAnalysisType } from 'utils/constants/issue-ai-status';

const markdownComponents = {
  img({ node, ...props}) {
    return <div style={{ maxWidth:'100%' }}>
      <Image src={props.src} />
    </div>
  },
  th: ({ node, ...props }) => (
    <th style={{ border: '1px solid #aaa', padding: '8px', textAlign: 'left' }} {...props}>
      {props.children}
    </th>
  ),
  td: ({ node, ...props }) => (
    <td style={{ border: '1px solid #aaa', padding: '8px' }} {...props}>
      {props.children}
    </td>
  ),
};

export const IssueAiSolutionUtil = Object.freeze({
  /**
   * @return {{ rootCauseText: string, markdownText: string }}
   * */
  parseResult(answerText, solutionItemType) {
    if (!answerText) {
      return { rootCauseText: '', markdownText: '' };
    }

    const difyParsed = IssueAiSolutionUtil.parseDifyStepResult(answerText, solutionItemType);
    if (difyParsed.rootCauseText || difyParsed.markdownText) {
      return difyParsed;
    }
    return IssueAiSolutionUtil.parseMarkdownHeadingResult(answerText, solutionItemType);
  },

  parseDifyStepResult(answerText, solutionItemType) {
    if (!answerText) {
      return { rootCauseText: '', markdownText: '' };
    }

    // 解析sections逻辑
    const lines = answerText.split('\n');
    const sections = makeSectionsFromLinesByHeaderRegex(lines, /^(?:[#*]*\s*)(?:Step|修复建议|Fix Recommendations)/);

    let targetSection;
    if (solutionItemType === SolutionItemType.ROOT_CAUSE) {
      // 查找根因section
      targetSection = sections.find(section => {
        const { header } = section;
        let stepNumber;
        const match = header.match(/^#*\s*Step\s*(\d+):\s*(.*)$/);
        if (match) {
          stepNumber = Number(match[1]);
        }
        return stepNumber === 4 || header.includes(ze('根因结论', 'Root Cause Conclusion'));
      });
    } else if (solutionItemType === SolutionItemType.SUGGESTION) {
      // 查找建议section
      targetSection = sections.find(section => {
        const { header } = section;
        let stepNumber;
        const match = header.match(/^#*\s*Step\s*(\d+):\s*(.*)$/);
        if (match) {
          stepNumber = Number(match[1]);
        }
        return !stepNumber && header.includes(ze('修复建议', 'Fix Recommendations'));
      });
    }

    if (!targetSection) {
      return { rootCauseText: '', markdownText: '' };
    }

    const { bodyLines } = targetSection;
    let rootCauseText;
    let markdownText;

    if (solutionItemType === SolutionItemType.ROOT_CAUSE) {
      const rootCauseIndex = bodyLines.findIndex(line => line.includes('最可能根因'));
      if (rootCauseIndex !== -1 && rootCauseIndex + 1 < bodyLines.length) {
        rootCauseText = bodyLines[rootCauseIndex + 1];
        const updatedBodyLines = [...bodyLines];
        updatedBodyLines.splice(rootCauseIndex, 2);
        markdownText = updatedBodyLines.join('\n');
      } else {
        markdownText = bodyLines.join('\n');
      }
    } else {
      markdownText = bodyLines.join('\n');
    }

    return {
      rootCauseText,
      markdownText,
    };
  },

  parseMarkdownHeadingResult(answerText, solutionItemType) {
    if (!answerText) {
      return { rootCauseText: '', markdownText: '' };
    }
    const lines = answerText.split('\n');
    const sections = makeSectionsFromLinesByHeaderRegex(
      lines,
      /^(?:[#*]*\s*)(?:根因结论|Root Cause|修复建议|Fix Recommendations)\b/,
    );

    let targetSection;
    if (solutionItemType === SolutionItemType.ROOT_CAUSE) {
      targetSection = sections.find(section => /根因结论|(?:^|[#*\s])Root Cause(?!\s*Recommendations)/i.test(section.header));
    } else if (solutionItemType === SolutionItemType.SUGGESTION) {
      targetSection = sections.find(section => /修复建议|Fix Recommendations/i.test(section.header));
    }

    if (!targetSection) {
      return { rootCauseText: '', markdownText: '' };
    }

    const { bodyLines } = targetSection;
    let rootCauseText;
    let markdownText;
    if (solutionItemType === SolutionItemType.ROOT_CAUSE) {
      const rootCauseIndex = bodyLines.findIndex(line => line.includes('最可能根因'));
      if (rootCauseIndex !== -1 && rootCauseIndex + 1 < bodyLines.length) {
        rootCauseText = bodyLines[rootCauseIndex + 1];
        const updatedBodyLines = [...bodyLines];
        updatedBodyLines.splice(rootCauseIndex, 2);
        markdownText = updatedBodyLines.join('\n');
      } else {
        markdownText = bodyLines.join('\n');
      }
    } else {
      markdownText = bodyLines.join('\n');
    }

    return {
      rootCauseText,
      markdownText,
    };
  },
});

export const IssueAiSolutionHooks = Object.freeze({
  useFetchLatestAiChatMessage(appId, issueId) {
    const [latestMessage, setLatestMessage] = useState(null);
    // 需求点 4 方向 B：监听 AI Agent 针对本 Issue 完成分析的 refresh trigger，自动重新拉取卡片数据。
    const issueAnalysisRefreshTrigger = useZuStore(state => state.aiDrawer.issueAnalysisRefreshTrigger);
    const issueAnalysisRefreshIssueId = useZuStore(state => state.aiDrawer.issueAnalysisRefreshIssueId);

    async function fetchLastAiChatMessage() {
      try {
        const rsp = await RestHelper.mustPost('/api/aiPilot/getLatestAiMessage', {
          appId,
          issueId,
        });
        let latestMessageObj = rsp.json.data?.data?.[0];
        if (latestMessageObj?.answer) {
          latestMessageObj.answer = AiIssueAnalysisUtil.convertAnswerToMarkdownText(latestMessageObj.answer);
        }
        if (latestMessageObj) {
          const wrapper = rsp.json.data;
          if (!latestMessageObj.analysisType && wrapper?.analysisType) {
            latestMessageObj.analysisType = wrapper.analysisType;
          }
          if (!latestMessageObj.workspaceUid && wrapper?.workspaceUid) {
            latestMessageObj.workspaceUid = wrapper.workspaceUid;
          }
          if (!latestMessageObj.conversationId && !latestMessageObj.conversation_id && wrapper?.conversationId) {
            latestMessageObj.conversationId = wrapper.conversationId;
          }
        }
        setLatestMessage(latestMessageObj || null);
      } catch (error) {
        console.error('Failed to fetch latest AI message:', error);
      }
    }

    useEffect(() => {
      if (appId && issueId) {
        fetchLastAiChatMessage();
      }
    }, [appId, issueId, issueAnalysisRefreshTrigger, issueAnalysisRefreshIssueId]);

    return latestMessage;
  },

      /**
   * @return {{ rootCauseText: string, markdownText: string }}
   * */
  useParsedResult(answerText, solutionItemType) {
    return useMemo(() => {
      return IssueAiSolutionUtil.parseResult(answerText, solutionItemType);
    }, [answerText, solutionItemType]);
  },
});

const SolutionItemType = {
  // 根因分析
  ROOT_CAUSE: 'ROOT_CAUSE',
  // 修复建议
  SUGGESTION: 'SUGGESTION',
};

export const IssueAiSolutionItemType = SolutionItemType;

const SolutionItemCard = ({
  itemType,
  appId,
  issueId,
  text,
  aiStatus,
  analysisType,
  workspaceUid,
  conversationId,
}) => {
  const aiDrawerSlice = useZuStore(state => state.aiDrawer);
  const isCrashClaw = AiAnalysisType.isCrashClaw(analysisType);

  let stepIcon = null;
  let title;
  if (itemType === SolutionItemType.ROOT_CAUSE) {
    stepIcon = <RootCauseIcon width={16} height={16} />;
    title = ze('根因结论', 'Root Cause Conclusion');
  } else if (itemType === SolutionItemType.SUGGESTION) {
    stepIcon = <SuggestionIcon width={16} height={16}  />;
    title = ze('修复建议', 'Fix Recommendations');
  }

  const { rootCauseText, markdownText } = IssueAiSolutionHooks.useParsedResult(text, itemType);

  function onClickViewMore() {
    if (isCrashClaw && workspaceUid && conversationId) {
      aiDrawerSlice.startChatBotWithCrashClaw({
        workspaceUid,
        sessionId: conversationId,
      });
      return;
    }
    aiDrawerSlice.startChatBotWithIssue(issueId, appId);
  }

  const displayAiStatus = isCrashClaw ? IssueAiStatus.USER_GENERATED : aiStatus;

  const renderAiStatusTag = () => {
    let tagText = '';
    let tooltipText = '';
    
    if (displayAiStatus === IssueAiStatus.USER_GENERATED) {
      tagText = ze('在线分析', 'Online Analysis');
      tooltipText = ze('基于堆栈等信息直接进行分析', 'Analysis based on stack traces and other information');
    } else if (displayAiStatus === IssueAiStatus.SYSTEM_GENERATED) {
      tagText = ze('离线分析', 'Offline Analysis');
      tooltipText = ze('本地拉取源码片段结合堆栈等信息进行分析', 'Local source code analysis combined with stack traces and other information');
    } else {
      return null;
    }

    return (
      <Tag color="geekblue" style={{ marginLeft: '8px' }}>
        {tagText}
        <Tooltip title={tooltipText}>
          <InfoCircleOutlined style={{ marginLeft: '4px' }} />
        </Tooltip>
      </Tag>
    );
  };

  const renderEngineTag = () => {
    const tagText = isCrashClaw
      ? ze('CrashClaw问题分析', 'CrashClaw Issue Analysis')
      : ze('CrashSight问题分析', 'CrashSight Issue Analysis');
    return (
      <Tag color="purple" style={{ marginLeft: '8px' }}>
        {tagText}
      </Tag>
    );
  };

  return <Card
    bodyStyle={{ padding: '16px' }}
  >
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
        { stepIcon }
        <div style={{ marginLeft: '8px', fontWeight: 700 }}>{ title }</div>
        { renderAiStatusTag() }
        { renderEngineTag() }
        <div style={{ flexGrow: 1 }} />
        <Button
          type='link'
          style={{ padding: '0', height: 'inherit', lineHeight: 'inherit' }}
          onClick={() => onClickViewMore()}
        >{ ze('查看更多', 'View More') }</Button>
      </div>
      <div style={{ flexGrow: 1, height: '80px', marginLeft: '24px', overflowY: 'auto' }}>
        { rootCauseText && <div
          /* style={{
            backgroundColor: '#ffd8d2',
            borderRadius: '4px',
            padding: '8px 12px',
            marginBottom: '8px',
          }} */
        >{ze('最可能根因', 'Most Likely Root Cause')}：{ rootCauseText }</div>}
        { !rootCauseText && <ReactMarkdown
          remarkPlugins={[gfm]}
          linkTarget='_blank'
          children={markdownText}
          components={markdownComponents}
        /> }
      </div>
    </div>
  </Card>;
};

const IssueAiSolution = ({
  appId,
  issueId,
  chatMessage,
  reduxState,
}) => {
  const currentIssueObj = useMemo(() => reduxState.issue.get('current').toJS(), [reduxState.issue.get('current')]) || {};
  const aiStatus = currentIssueObj?.esMap?.aiStatus; // 当前issue的AI分析状态

  const rawText = chatMessage?.answer || '';
  const analysisType = chatMessage?.analysisType;
  const workspaceUid = chatMessage?.workspaceUid;
  const conversationId = chatMessage?.conversationId || chatMessage?.conversation_id;

  const text = AiDrawerHooks.useConvertAnswerToMarkdownText(rawText);

  // 使用hook检查是否存在对应的sections
  const { rootCauseText: hasRootCause, markdownText: rootCauseMarkdown } = IssueAiSolutionHooks.useParsedResult(text, SolutionItemType.ROOT_CAUSE);
  const { rootCauseText: hasSuggestion, markdownText: suggestionMarkdown } = IssueAiSolutionHooks.useParsedResult(text, SolutionItemType.SUGGESTION);

  const hasValidRootCause = hasRootCause || rootCauseMarkdown;
  const hasValidSuggestion = hasSuggestion || suggestionMarkdown;

  if (!hasValidRootCause && !hasValidSuggestion) {
    return null;
  }

  const span = 24 / [hasValidRootCause, hasValidSuggestion].filter(x => x).length;

  return (
    <Row gutter={16} align='top'>
      { hasValidRootCause && <Col span={span}>
        <SolutionItemCard
          itemType={SolutionItemType.ROOT_CAUSE}
          appId={appId}
          issueId={issueId}
          text={text}
          aiStatus={aiStatus}
          analysisType={analysisType}
          workspaceUid={workspaceUid}
          conversationId={conversationId}
        />
      </Col> }
      { hasValidSuggestion && <Col span={span}>
        <SolutionItemCard
          itemType={SolutionItemType.SUGGESTION}
          appId={appId}
          issueId={issueId}
          text={text}
          aiStatus={aiStatus}
          analysisType={analysisType}
          workspaceUid={workspaceUid}
          conversationId={conversationId}
        />
      </Col> }
    </Row>
  );
};


const mapStateToProps = (state, router) => ({
  reduxState: state,
});

export default connect(mapStateToProps)(IssueAiSolution);
